import React, { FC, useEffect, useState } from 'react';
import {
  BaseLevels,
  BaseSizes,
  Button,
  ButtonVariant,
  ExtraLevels,
  Heading,
  Icon,
  IconName,
  Modal,
  Text,
  TextVariant,
  TextWeight,
} from '@kb-front/ui-kit';

import { Panel } from '@kapital-bank/kb-ui-design-system';

import CliProtect from 'shared/assets/images/cli-protect.svg';
import { IInsurance, ISelectedInsurance, ITariff } from 'shared/model';
import { translate } from 'shared/utils/translation';

import InsuranceList from '../InsuranceList';

import './index.scss';

interface LifeInsuranceProps {
  insuranceListForPossibleMonths: IInsurance[];
  commonInfo?: string;
  activeMonthPeriod?: string;
  selectedInsuranceData: ISelectedInsurance | null;
  wantedAmount: number;
  setSelectedInsuranceData: (value: ISelectedInsurance | null) => void;
  setInsuranceAmount: (value: number) => void;
}

const LifeInsurance: FC<LifeInsuranceProps> = ({
  insuranceListForPossibleMonths,
  commonInfo,
  activeMonthPeriod,
  wantedAmount,
  selectedInsuranceData,
  setSelectedInsuranceData,
  setInsuranceAmount,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [insuranceDetailsVisible, setInsuranceDetailsVisible] = useState(false);
  const [hasActiveInsurance, setHasActiveInsurance] = useState<number>();

  const showAddInsuranceButton = selectedInsuranceData?.name !== 'doNotNeedInsurance';
  const selectedInsurance = insuranceListForPossibleMonths?.find((insuranceItem: IInsurance) => insuranceItem.name === selectedInsuranceData?.title);
  const selectedInsurancePercentForPeriod = Number(
    selectedInsurance?.tariffs.find((tarif: ITariff) => tarif.duration === Number(activeMonthPeriod))?.tariff?.toFixed(2)
  );
  const currentMinInsuranceFee = selectedInsurance?.minInsuranceFee;
  const insuranceAmount = (wantedAmount * selectedInsurancePercentForPeriod) / 100;
  let updateSelectedInsuranceAmount = insuranceAmount;

  if (currentMinInsuranceFee && insuranceAmount < currentMinInsuranceFee) {
    updateSelectedInsuranceAmount = currentMinInsuranceFee;
  }

  useEffect(() => {
    setInsuranceAmount(updateSelectedInsuranceAmount);
    setHasActiveInsurance(updateSelectedInsuranceAmount);
  }, [activeMonthPeriod, insuranceListForPossibleMonths, updateSelectedInsuranceAmount, selectedInsuranceData]);

  return (
    <Panel className="life-insurance" padding={3}>
      <Panel className="life-insurance__info" marginBottom={3}>
        <Panel className="life-insurance__info__text-wrapper">
          <Text weight={TextWeight.SEMI_BOLD} level={BaseLevels.L2} block>
            {translate('lifeInsurance.description')}
          </Text>
          <div className="life-insurance__info__text-wrapper__percent">
            <div className="success-checked">
              <Icon iconName={IconName.CHECK} size={BaseSizes.M} variant={TextVariant.SUCCESS} />
            </div>
            <Text variant={TextVariant.SECONDARY} level={BaseLevels.L3}>
              {translate('lifeInsurance.guarantor')}
            </Text>
          </div>
          <Panel marginTop={2}>
            <Text variant={TextVariant.SECONDARY} weight={TextWeight.SEMI_BOLD}>
              {translate('lifeInsurance.details.title')}{' '}
              <span className="details">
                <Text variant={TextVariant.LINK} onClick={() => setInsuranceDetailsVisible(true)}>
                  {translate('lifeInsurance.details.link')}{' '}
                </Text>
              </span>
            </Text>
          </Panel>
        </Panel>
        <Panel>
          <img src={CliProtect} alt="cli-protect" />
        </Panel>
      </Panel>
      <div className="life-insurance__border" />

      <Panel className="life-insurance__added-insurance" marginTop={5}>
        <Panel>
          {selectedInsuranceData?.name === 'doNotNeedInsurance' ? (
            <Panel className="success_insurance">
              <Panel className="success_insurance__not-need">
                <Text variant={TextVariant.SECONDARY} weight={TextWeight.THIN} block>
                  {selectedInsuranceData?.title}
                </Text>
                <Panel marginLeft={1}>
                  <Icon iconName={IconName.PEN} size={BaseSizes.L} variant={TextVariant.SECONDARY} onClick={() => setIsVisible(true)} />
                </Panel>
              </Panel>

              <div>
                <Button variant={ButtonVariant.GHOST} onClick={() => setSelectedInsuranceData(null)}>
                  {translate('lifeInsurance.delete')}
                </Button>
              </div>
            </Panel>
          ) : null}
        </Panel>
        <Panel>
          {updateSelectedInsuranceAmount ? (
            <Panel className="success_insurance">
              <div className="success_insurance__info">
                <Panel marginRight={1}>
                  <img src={selectedInsuranceData?.imageName} alt="" className="success_insurance__info-img" />
                </Panel>
                <Panel className="success_insurance">
                  <Panel marginRight={3}>
                    <Text weight={TextWeight.SEMI_BOLD} block>
                      {Number(updateSelectedInsuranceAmount?.toFixed(2))} ₼
                    </Text>
                    <Text variant={TextVariant.SECONDARY} weight={TextWeight.THIN}>
                      {selectedInsuranceData?.title}
                    </Text>
                  </Panel>
                  <div className="success_insurance-edit">
                    <Icon iconName={IconName.PEN} size={BaseSizes.L} variant={TextVariant.SECONDARY} onClick={() => setIsVisible(true)} />
                  </div>
                </Panel>
              </div>

              <div>
                <Button variant={ButtonVariant.GHOST} onClick={() => setSelectedInsuranceData(null)}>
                  {translate('lifeInsurance.delete')}
                </Button>
              </div>
            </Panel>
          ) : (
            showAddInsuranceButton && (
              <div>
                <Button variant={ButtonVariant.GHOST} onClick={() => setIsVisible(true)} block>
                  {translate('lifeInsurance.addInsurance')}
                </Button>
              </div>
            )
          )}
        </Panel>
      </Panel>
      {isVisible && (
        <InsuranceList
          setIsVisible={setIsVisible}
          setSelectedInsuranceData={setSelectedInsuranceData}
          insuranceListForPossibleMonths={insuranceListForPossibleMonths}
          activeMonthPeriod={activeMonthPeriod}
          wantedAmount={wantedAmount}
          selectedInsuranceData={selectedInsuranceData}
          hasActiveInsurance={hasActiveInsurance}
        />
      )}

      <Modal
        onClose={() => setInsuranceDetailsVisible(false)}
        visible={insuranceDetailsVisible}
        size={BaseSizes.M}
        header={
          <Heading level={ExtraLevels.L5}>
            <div className="insurance-list__title">{translate('lifeInsurance.modal.detailsTitle')}</div>
          </Heading>
        }
      >
        <Text>
          {commonInfo?.split('\n').map(line => (
            <Text key={line}>
              {line}
              <br />
            </Text>
          ))}
        </Text>
      </Modal>
    </Panel>
  );
};

export default LifeInsurance;
